import React from 'react';

import FaExternalLink from 'react-icons/lib/fa/external-link';
import FaLock from 'react-icons/lib/fa/lock';

import {
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    Typography,
    WDSButton,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import styles from './BookButton.scss';

import type { TFunction } from 'i18next';

const FADE_DURATION = 250;

interface ButtonContentsProps {
    className?: string;
    deeplink?: boolean;
    price?: string;
    t?: TFunction;
}

function ButtonContents({
    className,
    deeplink = false,
    price,
    t = () => '',
}: ButtonContentsProps) {
    return (
        <div className={`${className} ${styles.buttonContents}`}>
            <div className={styles.buttonText}>
                <span>{t<string>('Book')}</span>
                <span>&nbsp;|&nbsp;</span>
                <span data-id="price">
                    {price}
                </span>
            </div>
            {deeplink && (
                <FaExternalLink className={styles.deepLinkIcon} />
            )}
        </div>
    );
}

const LocalizedButtonContents = localize(ButtonContents, ['t'], 'baseApp');

interface DeepLinkData {
    method?: 'POST' | 'GET';
    href?: string;
    host?: string;
    body?: {
        reqXML?: string;
        partnerId?: string;
    };
}

interface I18nMoney {
    (prices: Record<string, number | string>[]): {
        getTotalPrice: () => number;
    };
    format: (amount: number) => string;
}

interface BookButtonProps {
    animateHeight?: boolean;
    busy?: boolean;
    buttonContentsClassName?: string;
    className?: string;
    deepLinkData?: DeepLinkData;
    deeplink?: boolean;
    deeplinkExists?: boolean;
    disabled?: boolean;
    href?: string;
    i18nMoney: I18nMoney;
    onClick?: (isDeeplink: boolean, href: string) => void;
    price?: string;
    show?: boolean;
    t: TFunction;
    tripPrices?: Record<string, number | string>[];
}

function BookButton({
    animateHeight = false,
    busy = false,
    buttonContentsClassName = '',
    className = '',
    deepLinkData = {},
    deeplink = false,
    deeplinkExists = false,
    disabled = false,
    href = '',
    i18nMoney,
    onClick = () => undefined,
    price,
    show = false,
    t,
    tripPrices = [],
}: BookButtonProps) {
    const linkButtonOpts = deeplink || deeplinkExists
        ? { target: '_blank' as const }
        : {};
    const buttonProps = {
        className: styles.bookButton,
        color: 'secondary',
        disabled: disabled || busy,
        fullWidth: true,
        label: t<string>('Book'),
        ...linkButtonOpts,
    };

    const total = i18nMoney(tripPrices).getTotalPrice();
    const displayPrice = total
        ? i18nMoney.format(total)
        : price;

    function onSubmit() {
        onClick(deeplink, href);
    }

    return (
        <FadeInOutContainer
            animateHeight={animateHeight}
            className={className}
            duration={FADE_DURATION}
        >
            <FadeInOut show={show}>
                <WDSButton
                    component={A}
                    href={href}
                    onClick={onSubmit}
                    underline="none"
                    {...buttonProps}
                >
                    <LocalizedButtonContents
                        className={buttonContentsClassName}
                        deeplink={deeplink}
                        price={displayPrice}
                    />
                </WDSButton>
                {deeplink && (
                    <div className={styles.helperText}>
                        <Typography variant="body2">
                            {t<string>('at-url', { website: deepLinkData.host })}
                        </Typography>
                        <FaLock className={styles.secure} />
                    </div>
                )}
            </FadeInOut>
        </FadeInOutContainer>
    );
}

export {
    BookButton,
    type BookButtonProps,
    ButtonContents,
};

export default localize(BookButton, ['i18nMoney', 't'], 'baseApp');
